/**
 * Platform-admin API — the remaining eastview-os `/admin/*` routers that aren't
 * reference data:
 *
 *   /admin/schemas             — app/routers/admin_schemas.py
 *       Extraction target schemas (code defaults + user customizations) and the
 *       read-only LOS canonical writeback classes. `_source` on each row is
 *       "code_default" | "customized" | "los_canonical"; los_canonical rows are
 *       read-only (the backend 400s on create/update against them).
 *
 *   /admin/extractors          — app/routers/admin_extractors.py
 *       Read-only view of the LIVE extraction routing config: one row per
 *       docType with its ordered pipeline steps, CU analyzer, LLM target,
 *       dedicated skill extractor, and 30-day run stats.
 *
 *   /admin/graph-subscriptions — app/routers/admin_graph_subscriptions.py
 *       Per-user Microsoft Graph mailbox subscriptions (CRM tracking /
 *       deal-reply). Creates + deletes proxy through the listener service, so
 *       they 502 with "listener error" / 503 "listener not configured" when the
 *       listener is down — surface that message rather than a generic failure.
 */
import { losHttp } from "../lib/losHttp";

/**
 * User activity rollup — GET /auth/admin/user-activity (user-activity-view.md).
 * A list of user×day entries with a categorized event breakdown
 * (view_events / write_events / categories{...}, loans_touched, loan_count).
 * `days` sets the look-back window; `group` (an org-group name/id) restricts rows
 * to that group's members (omit for all users).
 *
 * Served by the LOS main app on the `auth` router (prefix `/auth`), so the path is
 * `/auth/admin/user-activity` — NOT bare `/admin/user-activity`. The listener has
 * an identical implementation, but per backend-owner direction (2026-08-24) the
 * frontend calls the LOS route only — no direct FE→listener calls. `_require_admin`
 * gates it, so a non-admin bearer gets a 403.
 */
export const adminUserActivityApi = {
  list: ({ days = 30, group } = {}) =>
    losHttp
      .get("/auth/admin/user-activity", {
        params: { days, ...(group ? { group } : {}) },
      })
      .then((r) => r.data),

  /**
   * Complete actor/day audit feed — GET /auth/admin/user-activity/timeline
   * (user-activity-timeline-complete.md). ONE request returns every structured
   * audit event `user` produced on the `date` (YYYY-MM-DD, interpreted in
   * America/New_York by the backend, DST-correct), across BOTH pipelines and
   * regardless of whether the loan still exists — no loan cap, no pagination,
   * no pipeline join. Each event carries the server's `category`, `dealId`,
   * `loanNumber` / `loanName` / `sync_loan_id` (nullable), `pipeline` and a
   * `link` (`/loan/{id}` or `/acquisition/{id}`, null when the loan has no
   * current record). Same admin gate as the rollup; a bad `date` is a 422.
   */
  timeline: ({ user, date }) =>
    losHttp
      .get("/auth/admin/user-activity/timeline", { params: { user, date } })
      .then((r) => r.data),
};

export const adminSchemasApi = {
  // GET /admin/schemas → every schema row (extraction + LOS canonical)
  list: () => losHttp.get("/admin/schemas").then((r) => r.data || []),

  // GET /admin/schemas/{slug}
  get: (slug) => losHttp.get(`/admin/schemas/${slug}`).then((r) => r.data),

  // GET /admin/schemas/{slug}/field-mapping → the FieldSource registry ties
  // (forward for LOS canonical classes, inverse for extraction targets)
  fieldMapping: (slug) =>
    losHttp.get(`/admin/schemas/${slug}/field-mapping`).then((r) => r.data),

  // GET /admin/schemas/{slug}/preview → { json_schema, cu_fields, pydantic_class }
  preview: (slug) =>
    losHttp.get(`/admin/schemas/${slug}/preview`).then((r) => r.data),

  // POST /admin/schemas — body is wrapped in `schema` (CreateSchemaPayload alias).
  // 409 if the slug is already customized; 400 for LOS canonical names.
  create: (definition) =>
    losHttp.post("/admin/schemas", { schema: definition }).then((r) => r.data),

  // PUT /admin/schemas/{slug} — upserts the customization, bumps `version`
  update: (slug, definition) =>
    losHttp
      .put(`/admin/schemas/${slug}`, { schema: definition })
      .then((r) => r.data),

  // DELETE /admin/schemas/{slug} — drops the customization (the code default
  // takes over again). 404 when there is no customization to delete.
  remove: (slug) =>
    losHttp.delete(`/admin/schemas/${slug}`).then((r) => r.data),
};

export const adminExtractorsApi = {
  // GET /admin/extractors → { generatedAt, windowDays, methodGlossary, totals, rows }
  list: () => losHttp.get("/admin/extractors").then((r) => r.data),
};

// The two purposes the backend accepts (PURPOSES in the router); anything else 400s.
export const GRAPH_SUBSCRIPTION_PURPOSES = ["crm-tracking", "deal-reply"];

export const adminGraphSubscriptionsApi = {
  // GET /admin/graph-subscriptions → rows straight from Mongo (status != deleted)
  list: () =>
    losHttp
      .get("/admin/graph-subscriptions")
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  // POST /admin/graph-subscriptions {userEmail, purpose?, richData?}
  create: (body) =>
    losHttp
      .post("/admin/graph-subscriptions", body, { timeout: 60 * 1000 })
      .then((r) => r.data),

  // DELETE /admin/graph-subscriptions/{userEmail} — removes ALL subscriptions
  // for that mailbox.
  remove: (userEmail) =>
    losHttp
      .delete(`/admin/graph-subscriptions/${encodeURIComponent(userEmail)}`, {
        timeout: 60 * 1000,
      })
      .then((r) => r.data),
};

export const adminPlatformApi = {
  schemas: adminSchemasApi,
  extractors: adminExtractorsApi,
  graphSubscriptions: adminGraphSubscriptionsApi,
};

export default adminPlatformApi;
